import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import {
  Background,
  BaseEdge,
  Handle,
  Panel,
  Position,
  ReactFlow,
  getSmoothStepPath,
  type Edge,
  type EdgeProps,
  type EdgeTypes,
  type Node,
  type NodeProps,
  type NodeTypes,
} from '@xyflow/react';
import { motion } from 'motion/react';
import type { TagOverviewResponse, TagOverviewTag } from '@/services/tagOverview';
import { colors } from '@/theme/colors';

type TagOverviewGraphProps = {
  overview: TagOverviewResponse;
  selectedTagIds: number[];
  onSelectionChange: (tagIds: number[]) => void;
  loading?: boolean;
  error?: string | null;
  onRetry?: () => void;
  fullscreen?: boolean;
};

type RelationState = 'selected' | 'related' | 'preview' | 'default' | 'muted';

type BaseTagNodeData = {
  tagId: number;
  label: string;
  color: string;
  role: 'source' | 'intermediate' | 'outcome' | 'isolated';
  sheet: number;
  nodeCount: number;
  degree: number;
  width: number;
  height: number;
  sourceHandles: Array<{ id: string; left: string }>;
  targetHandles: Array<{ id: string; left: string }>;
  relationState: RelationState;
  revealIndex: number;
};

type TagNodeData = BaseTagNodeData & {
  reducedMotion: boolean;
  fullscreen: boolean;
  onToggle: (tagId: number) => void;
};

type GraphEdgeData = {
  color: string;
  relationState: RelationState;
  reducedMotion: boolean;
  hasReciprocal: boolean;
  sourceTagId: number;
  targetTagId: number;
};

type GraphBuildResult = {
  nodes: Node<BaseTagNodeData>[];
  edges: Edge<GraphEdgeData>[];
  orderedTags: TagOverviewTag[];
  heroCenter: { x: number; y: number };
};

type ViewportState = {
  x: number;
  y: number;
  zoom: number;
};

const TAG_COLOR_MAP: Record<string, string> = {
  'var(--tag-red)': '#C96B63',
  'var(--tag-purple)': '#8C6ACF',
  'var(--tag-blue)': '#5A87D9',
  'var(--tag-salad)': '#7DAA57',
  'var(--tag-yellow)': '#D6A645',
};

const LAYER_GAP = 126;
const FULLSCREEN_LAYER_GAP = 150;
const NODE_GAP = 42;
const FULLSCREEN_NODE_GAP = 56;
const COMPONENT_GAP = 170;
const FULLSCREEN_COMPONENT_GAP = 230;
const SATELLITE_COMPONENT_GAP = 250;
const GRAPH_PADDING_X = 80;
const GRAPH_PADDING_Y = 72;

function hexToRgba(hex: string, alpha: number): string {
  const normalized = hex.replace('#', '');
  const full = normalized.length === 3
    ? normalized.split('').map((char) => `${char}${char}`).join('')
    : normalized.padEnd(6, '0').slice(0, 6);
  const red = Number.parseInt(full.slice(0, 2), 16);
  const green = Number.parseInt(full.slice(2, 4), 16);
  const blue = Number.parseInt(full.slice(4, 6), 16);
  return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
}

function withAlpha(color: string, alpha: number): string {
  return hexToRgba(resolveTagColor(color), alpha);
}

function resolveTagColor(color: string): string {
  if (color.startsWith('#')) return color;
  if (TAG_COLOR_MAP[color]) return TAG_COLOR_MAP[color];
  return colors.grey.sheetFallback;
}

function usePrefersReducedMotion() {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;

    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setPrefersReducedMotion(mediaQuery.matches);
    update();
    mediaQuery.addEventListener('change', update);
    return () => mediaQuery.removeEventListener('change', update);
  }, []);

  return prefersReducedMotion;
}

function getTagDimensions(nodeCount: number, minCount: number, maxCount: number) {
  if (maxCount <= minCount) {
    return {
      width: 244,
      height: 84,
    };
  }

  const normalized = (nodeCount - minCount) / (maxCount - minCount);
  const eased = Math.sqrt(Math.max(0, normalized));

  return {
    width: Math.round(220 + eased * 152),
    height: Math.round(72 + eased * 38),
  };
}

function buildMaps(tags: TagOverviewTag[], edges: TagOverviewResponse['edges']) {
  const tagIds = new Set(tags.map((tag) => tag.id));
  const incoming = new Map<number, number[]>();
  const outgoing = new Map<number, number[]>();
  const undirected = new Map<number, Set<number>>();

  for (const tag of tags) {
    incoming.set(tag.id, []);
    outgoing.set(tag.id, []);
    undirected.set(tag.id, new Set());
  }

  for (const edge of edges) {
    if (!tagIds.has(edge.source_tag_id) || !tagIds.has(edge.target_tag_id)) continue;
    incoming.get(edge.target_tag_id)?.push(edge.source_tag_id);
    outgoing.get(edge.source_tag_id)?.push(edge.target_tag_id);
    undirected.get(edge.source_tag_id)?.add(edge.target_tag_id);
    undirected.get(edge.target_tag_id)?.add(edge.source_tag_id);
  }

  return { incoming, outgoing, undirected };
}

function getNodePriority(
  tagId: number,
  tagsById: Map<number, TagOverviewTag>,
  incoming: Map<number, number[]>,
  outgoing: Map<number, number[]>,
  minNodeCount: number,
  maxNodeCount: number
) {
  const tag = tagsById.get(tagId);
  if (!tag) return 0;

  const degree = (incoming.get(tagId)?.length ?? 0) + (outgoing.get(tagId)?.length ?? 0);
  const normalizedNodeCount = maxNodeCount <= minNodeCount
    ? 0.5
    : (tag.node_count - minNodeCount) / (maxNodeCount - minNodeCount);

  return normalizedNodeCount * 0.58 + Math.min(degree, 8) / 8 * 0.42;
}

function getNodeRole(tagId: number, incoming: Map<number, number[]>, outgoing: Map<number, number[]>) {
  const incomingCount = incoming.get(tagId)?.length ?? 0;
  const outgoingCount = outgoing.get(tagId)?.length ?? 0;

  if (incomingCount === 0 && outgoingCount === 0) return 'isolated' as const;
  if (outgoingCount === 0) return 'outcome' as const;
  if (incomingCount === 0) return 'source' as const;
  return 'intermediate' as const;
}

function getSingleNodeLayerShift(layerIndex: number, totalLayers: number) {
  if (totalLayers <= 2) return 0;
  if (layerIndex === 0 || layerIndex === totalLayers - 1) return 0;

  const distanceFromEdge = Math.min(layerIndex, totalLayers - 1 - layerIndex);
  const amplitude = 150 + Math.max(0, distanceFromEdge - 1) * 42;
  return layerIndex % 2 === 1 ? -amplitude : amplitude;
}

function buildHandleLayout(count: number, prefix: string) {
  if (count <= 0) {
    return [{ id: `${prefix}-0`, left: '50%' }];
  }

  if (count === 1) {
    return [{ id: `${prefix}-0`, left: '50%' }];
  }

  const start = 18;
  const end = 82;
  const step = (end - start) / (count - 1);
  return Array.from({ length: count }, (_, index) => ({
    id: `${prefix}-${index}`,
    left: `${start + step * index}%`,
  }));
}

function compareTagIds(
  a: number,
  b: number,
  tagsById: Map<number, TagOverviewTag>,
  incoming: Map<number, number[]>,
  outgoing: Map<number, number[]>,
  priorityByTagId: Map<number, number>
) {
  const byPriority = (priorityByTagId.get(b) ?? 0) - (priorityByTagId.get(a) ?? 0);
  if (Math.abs(byPriority) > 0.001) return byPriority;

  const byIncoming = (incoming.get(b)?.length ?? 0) - (incoming.get(a)?.length ?? 0);
  if (byIncoming !== 0) return byIncoming;

  const byOutgoing = (outgoing.get(b)?.length ?? 0) - (outgoing.get(a)?.length ?? 0);
  if (byOutgoing !== 0) return byOutgoing;

  return (tagsById.get(a)?.title ?? '').localeCompare(tagsById.get(b)?.title ?? '');
}

function getComponents(tags: TagOverviewTag[], undirected: Map<number, Set<number>>) {
  const unvisited = new Set(tags.map((tag) => tag.id));
  const components: number[][] = [];

  while (unvisited.size > 0) {
    const start = unvisited.values().next().value as number;
    const queue = [start];
    const component: number[] = [];
    unvisited.delete(start);

    while (queue.length > 0) {
      const current = queue.shift();
      if (current == null) continue;
      component.push(current);

      for (const next of undirected.get(current) ?? []) {
        if (!unvisited.has(next)) continue;
        unvisited.delete(next);
        queue.push(next);
      }
    }

    components.push(component);
  }

  return components;
}

function getTopologicalLevels(
  componentTagIds: number[],
  incoming: Map<number, number[]>,
  outgoing: Map<number, number[]>
) {
  const componentSet = new Set(componentTagIds);
  const indegree = new Map<number, number>();

  for (const tagId of componentTagIds) {
    indegree.set(
      tagId,
      (incoming.get(tagId) ?? []).filter((parentId) => componentSet.has(parentId)).length
    );
  }

  const queue = componentTagIds.filter((tagId) => (indegree.get(tagId) ?? 0) === 0);
  const ordered: number[] = [];

  while (queue.length > 0) {
    const current = queue.shift();
    if (current == null) continue;
    ordered.push(current);

    for (const childId of outgoing.get(current) ?? []) {
      if (!componentSet.has(childId)) continue;
      const nextIndegree = (indegree.get(childId) ?? 0) - 1;
      indegree.set(childId, nextIndegree);
      if (nextIndegree === 0) {
        queue.push(childId);
      }
    }
  }

  if (ordered.length !== componentTagIds.length) {
    return null;
  }

  const levelByTagId = new Map<number, number>();
  for (const tagId of ordered) {
    const parentLevels = (incoming.get(tagId) ?? [])
      .filter((parentId) => componentSet.has(parentId))
      .map((parentId) => levelByTagId.get(parentId) ?? 0);
    levelByTagId.set(tagId, parentLevels.length > 0 ? Math.max(...parentLevels) + 1 : 0);
  }

  return levelByTagId;
}

function getStronglyConnectedComponents(
  componentTagIds: number[],
  outgoing: Map<number, number[]>
) {
  const componentSet = new Set(componentTagIds);
  const indexByTagId = new Map<number, number>();
  const lowLinkByTagId = new Map<number, number>();
  const stack: number[] = [];
  const onStack = new Set<number>();
  const stronglyConnectedComponents: number[][] = [];
  let nextIndex = 0;

  const visit = (tagId: number) => {
    indexByTagId.set(tagId, nextIndex);
    lowLinkByTagId.set(tagId, nextIndex);
    nextIndex += 1;
    stack.push(tagId);
    onStack.add(tagId);

    for (const neighborId of outgoing.get(tagId) ?? []) {
      if (!componentSet.has(neighborId)) continue;

      if (!indexByTagId.has(neighborId)) {
        visit(neighborId);
        lowLinkByTagId.set(
          tagId,
          Math.min(lowLinkByTagId.get(tagId) ?? 0, lowLinkByTagId.get(neighborId) ?? 0)
        );
      } else if (onStack.has(neighborId)) {
        lowLinkByTagId.set(
          tagId,
          Math.min(lowLinkByTagId.get(tagId) ?? 0, indexByTagId.get(neighborId) ?? 0)
        );
      }
    }

    if ((lowLinkByTagId.get(tagId) ?? -1) !== (indexByTagId.get(tagId) ?? -2)) return;

    const stronglyConnectedComponent: number[] = [];
    while (stack.length > 0) {
      const current = stack.pop();
      if (current == null) break;
      onStack.delete(current);
      stronglyConnectedComponent.push(current);
      if (current === tagId) break;
    }

    stronglyConnectedComponents.push(stronglyConnectedComponent);
  };

  for (const tagId of componentTagIds) {
    if (!indexByTagId.has(tagId)) {
      visit(tagId);
    }
  }

  return stronglyConnectedComponents;
}

function getCondensedLevels(
  stronglyConnectedComponents: number[][],
  outgoing: Map<number, number[]>
) {
  const componentIndexByTagId = new Map<number, number>();
  stronglyConnectedComponents.forEach((component, componentIndex) => {
    component.forEach((tagId) => componentIndexByTagId.set(tagId, componentIndex));
  });

  const condensedIncoming = new Map<number, Set<number>>();
  const condensedOutgoing = new Map<number, Set<number>>();
  const indegree = new Map<number, number>();

  stronglyConnectedComponents.forEach((_component, componentIndex) => {
    condensedIncoming.set(componentIndex, new Set());
    condensedOutgoing.set(componentIndex, new Set());
    indegree.set(componentIndex, 0);
  });

  stronglyConnectedComponents.forEach((component, componentIndex) => {
    for (const tagId of component) {
      for (const childId of outgoing.get(tagId) ?? []) {
        const childComponentIndex = componentIndexByTagId.get(childId);
        if (childComponentIndex == null || childComponentIndex === componentIndex) continue;
        if (!condensedOutgoing.get(componentIndex)?.has(childComponentIndex)) {
          condensedOutgoing.get(componentIndex)?.add(childComponentIndex);
          condensedIncoming.get(childComponentIndex)?.add(componentIndex);
          indegree.set(childComponentIndex, (indegree.get(childComponentIndex) ?? 0) + 1);
        }
      }
    }
  });

  const queue = stronglyConnectedComponents
    .map((_component, componentIndex) => componentIndex)
    .filter((componentIndex) => (indegree.get(componentIndex) ?? 0) === 0);
  const orderedComponents: number[] = [];

  while (queue.length > 0) {
    const current = queue.shift();
    if (current == null) continue;
    orderedComponents.push(current);

    for (const next of condensedOutgoing.get(current) ?? []) {
      const nextIndegree = (indegree.get(next) ?? 0) - 1;
      indegree.set(next, nextIndegree);
      if (nextIndegree === 0) queue.push(next);
    }
  }

  const levelByComponentIndex = new Map<number, number>();
  for (const componentIndex of orderedComponents) {
    const parentLevels = [...(condensedIncoming.get(componentIndex) ?? new Set<number>())]
      .map((parentIndex) => levelByComponentIndex.get(parentIndex) ?? 0);
    levelByComponentIndex.set(componentIndex, parentLevels.length > 0 ? Math.max(...parentLevels) + 1 : 0);
  }

  return { componentIndexByTagId, levelByComponentIndex };
}

function assignLayers(
  componentTagIds: number[],
  incoming: Map<number, number[]>,
  outgoing: Map<number, number[]>,
  priorityByTagId: Map<number, number>
) {
  const levelByTagId = getTopologicalLevels(componentTagIds, incoming, outgoing);

  if (!levelByTagId) {
    const stronglyConnectedComponents = getStronglyConnectedComponents(componentTagIds, outgoing);
    const { componentIndexByTagId, levelByComponentIndex } = getCondensedLevels(stronglyConnectedComponents, outgoing);
    const maxLevel = Math.max(...stronglyConnectedComponents.map((_, componentIndex) => levelByComponentIndex.get(componentIndex) ?? 0), 0);
    const layers = Array.from({ length: maxLevel + 1 }, () => [] as number[]);

    stronglyConnectedComponents.forEach((component, componentIndex) => {
      const orderedComponent = component
        .slice()
        .sort((a, b) => compareTagIds(a, b, new Map(), incoming, outgoing, priorityByTagId));
      const level = levelByComponentIndex.get(componentIndex) ?? 0;
      layers[level].push(...orderedComponent);
    });

    for (const layer of layers) {
      layer.sort((a, b) => {
        const aComponentIndex = componentIndexByTagId.get(a) ?? 0;
        const bComponentIndex = componentIndexByTagId.get(b) ?? 0;
        if (aComponentIndex !== bComponentIndex) {
          return compareTagIds(a, b, new Map(), incoming, outgoing, priorityByTagId);
        }
        return compareTagIds(a, b, new Map(), incoming, outgoing, priorityByTagId);
      });
    }

    return { layers };
  }

  const maxLevel = Math.max(...componentTagIds.map((tagId) => levelByTagId.get(tagId) ?? 0), 0);
  const layers = Array.from({ length: maxLevel + 1 }, () => [] as number[]);

  for (const tagId of componentTagIds) {
    layers[levelByTagId.get(tagId) ?? 0].push(tagId);
  }

  for (const layer of layers) {
    layer.sort((a, b) => compareTagIds(a, b, new Map(), incoming, outgoing, priorityByTagId));
  }

  return { layers };
}

function getNeighborCenter(
  neighborPositions: Map<number, number>,
  neighborIds: number[],
  priorityByTagId: Map<number, number>
) {
  const connectedNeighbors = neighborIds.filter((neighborId) => neighborPositions.has(neighborId));
  if (connectedNeighbors.length === 0) return Number.POSITIVE_INFINITY;

  let weightedSum = 0;
  let totalWeight = 0;
  for (const neighborId of connectedNeighbors) {
    const weight = 1 + (priorityByTagId.get(neighborId) ?? 0);
    weightedSum += (neighborPositions.get(neighborId) ?? 0) * weight;
    totalWeight += weight;
  }

  return totalWeight > 0 ? weightedSum / totalWeight : Number.POSITIVE_INFINITY;
}

function orderLayer(
  layer: number[],
  tagsById: Map<number, TagOverviewTag>,
  incoming: Map<number, number[]>,
  outgoing: Map<number, number[]>,
  anchorPositions: Map<number, number>,
  priorityByTagId: Map<number, number>,
  useOutgoingAnchors: boolean
) {
  return layer.slice().sort((a, b) => {
    const aNeighbors = useOutgoingAnchors ? (outgoing.get(a) ?? []) : (incoming.get(a) ?? []);
    const bNeighbors = useOutgoingAnchors ? (outgoing.get(b) ?? []) : (incoming.get(b) ?? []);
    const aCenter = getNeighborCenter(anchorPositions, aNeighbors, priorityByTagId);
    const bCenter = getNeighborCenter(anchorPositions, bNeighbors, priorityByTagId);

    if (Number.isFinite(aCenter) && Number.isFinite(bCenter) && aCenter !== bCenter) {
      return aCenter - bCenter;
    }

    if (Number.isFinite(aCenter) !== Number.isFinite(bCenter)) {
      return Number.isFinite(aCenter) ? -1 : 1;
    }

    return compareTagIds(a, b, tagsById, incoming, outgoing, priorityByTagId);
  });
}

function getLayerCrossingCount(
  upperLayer: number[],
  lowerLayer: number[],
  outgoing: Map<number, number[]>
) {
  const upperIndex = new Map(upperLayer.map((tagId, index) => [tagId, index]));
  const lowerIndex = new Map(lowerLayer.map((tagId, index) => [tagId, index]));
  const pairs: Array<{ upper: number; lower: number }> = [];

  for (const sourceId of upperLayer) {
    const sourceIndex = upperIndex.get(sourceId);
    if (sourceIndex == null) continue;

    for (const targetId of outgoing.get(sourceId) ?? []) {
      const targetIndex = lowerIndex.get(targetId);
      if (targetIndex == null) continue;
      pairs.push({ upper: sourceIndex, lower: targetIndex });
    }
  }

  let crossings = 0;
  for (let index = 0; index < pairs.length; index += 1) {
    for (let compareIndex = index + 1; compareIndex < pairs.length; compareIndex += 1) {
      const a = pairs[index];
      const b = pairs[compareIndex];
      if ((a.upper - b.upper) * (a.lower - b.lower) < 0) {
        crossings += 1;
      }
    }
  }

  return crossings;
}

function getPermutations(values: number[]) {
  if (values.length <= 1) return [values];

  const permutations: number[][] = [];
  values.forEach((value, index) => {
    const rest = values.slice(0, index).concat(values.slice(index + 1));
    getPermutations(rest).forEach((permutation) => {
      permutations.push([value, ...permutation]);
    });
  });

  return permutations;
}

function reduceLayerCrossings(
  layers: number[][],
  incoming: Map<number, number[]>,
  outgoing: Map<number, number[]>,
  tagsById: Map<number, TagOverviewTag>,
  priorityByTagId: Map<number, number>
) {
  const reducedLayers = layers.map((layer) => layer.slice());

  for (let sweep = 0; sweep < 2; sweep += 1) {
    for (let layerIndex = 1; layerIndex < reducedLayers.length; layerIndex += 1) {
      const layer = reducedLayers[layerIndex];
      if (layer.length <= 1 || layer.length > 6) continue;

      const anchorPositions = new Map<number, number>(
        reducedLayers[layerIndex - 1].map((tagId, index) => [tagId, index])
      );

      const seeded = orderLayer(
        layer,
        tagsById,
        incoming,
        outgoing,
        anchorPositions,
        priorityByTagId,
        false
      );

      let bestLayer = seeded;
      let bestScore = Number.POSITIVE_INFINITY;

      for (const permutation of getPermutations(seeded)) {
        const upperScore = getLayerCrossingCount(reducedLayers[layerIndex - 1], permutation, outgoing);
        const lowerScore = layerIndex < reducedLayers.length - 1
          ? getLayerCrossingCount(permutation, reducedLayers[layerIndex + 1], outgoing)
          : 0;
        const score = upperScore * 2 + lowerScore;

        if (score < bestScore) {
          bestScore = score;
          bestLayer = permutation;
        }
      }

      reducedLayers[layerIndex] = bestLayer;
    }
  }

  return reducedLayers;
}

function getRelationState(
  tagId: number,
  selectedSet: Set<number>,
  selectedRelatedSet: Set<number>,
  hoverSet: Set<number>
): RelationState {
  void selectedRelatedSet;
  void hoverSet;
  if (selectedSet.has(tagId)) return 'selected';
  return 'default';
}

function buildGraph(
  overview: TagOverviewResponse,
  selectedTagIds: number[],
  _hoveredTagId: number | null,
  fullscreen: boolean
): GraphBuildResult {
  const tagsById = new Map(overview.tags.map((tag) => [tag.id, tag]));
  const minNodeCount = Math.min(...overview.tags.map((tag) => tag.node_count), 1);
  const maxNodeCount = Math.max(...overview.tags.map((tag) => tag.node_count), 1);
  const dimensionsByTagId = new Map(
    overview.tags.map((tag) => [tag.id, getTagDimensions(tag.node_count, minNodeCount, maxNodeCount)])
  );
  const { incoming, outgoing, undirected } = buildMaps(overview.tags, overview.edges);
  const priorityByTagId = new Map(
    overview.tags.map((tag) => [tag.id, getNodePriority(tag.id, tagsById, incoming, outgoing, minNodeCount, maxNodeCount)])
  );
  const selectedSet = new Set(selectedTagIds);
  const selectedRelatedSet = new Set<number>();
  const hoverSet = new Set<number>();

  for (const tagId of selectedSet) {
    hoverSet.delete(tagId);
    selectedRelatedSet.add(tagId);
    for (const neighborId of undirected.get(tagId) ?? []) selectedRelatedSet.add(neighborId);
  }

  const components = getComponents(overview.tags, undirected);
  const layerGap = fullscreen ? FULLSCREEN_LAYER_GAP : LAYER_GAP;
  const nodeGap = fullscreen ? FULLSCREEN_NODE_GAP : NODE_GAP;
  const globalMaxNodeHeight = Math.max(...overview.tags.map((tag) => dimensionsByTagId.get(tag.id)?.height ?? 72), 72);
  const layerStride = globalMaxNodeHeight + layerGap;

  const componentLayouts = components.map((componentTagIds) => {
    const componentEdgeCount = componentTagIds.reduce((sum, tagId) => sum + (outgoing.get(tagId)?.length ?? 0), 0);
    const score = componentTagIds.reduce((sum, tagId) => sum + (priorityByTagId.get(tagId) ?? 0), 0) + componentEdgeCount * 0.35;
    const { layers } = assignLayers(componentTagIds, incoming, outgoing, priorityByTagId);
    const anchorPositions = new Map<number, number>();
    const nodes: Node<BaseTagNodeData>[] = [];
    const orderedLayers = reduceLayerCrossings(layers, incoming, outgoing, tagsById, priorityByTagId);
    const centerByTagId = new Map<number, number>();

    for (let layerIndex = orderedLayers.length - 1; layerIndex >= 0; layerIndex -= 1) {
      const orderedLayer = orderLayer(
        orderedLayers[layerIndex],
        tagsById,
        incoming,
        outgoing,
        anchorPositions,
        priorityByTagId,
        true
      );
      orderedLayers[layerIndex] = orderedLayer;

      const layerWidth = orderedLayer.reduce((sum, tagId, index) => {
        const width = dimensionsByTagId.get(tagId)?.width ?? 224;
        return sum + width + (index > 0 ? nodeGap : 0);
      }, 0);

      const childCenters = orderedLayer
        .flatMap((tagId) => (outgoing.get(tagId) ?? []).map((childId) => centerByTagId.get(childId)))
        .filter((value): value is number => value != null);
      const layerCenter = childCenters.length > 0
        ? childCenters.reduce((sum, value) => sum + value, 0) / childCenters.length
        : 0;
      const layerShift = orderedLayer.length === 1
        ? getSingleNodeLayerShift(layerIndex, orderedLayers.length)
        : 0;

      let cursorX = layerCenter - layerWidth / 2 + layerShift;
      anchorPositions.clear();
      for (const tagId of orderedLayer) {
        const dimensions = dimensionsByTagId.get(tagId);
        if (!dimensions) continue;
        const center = cursorX + dimensions.width / 2;
        anchorPositions.set(tagId, center);
        centerByTagId.set(tagId, center);
        cursorX += dimensions.width + nodeGap;
      }
    }

    const layerWidths = orderedLayers.map((layer) =>
      layer.reduce((sum, tagId, index) => {
        const width = dimensionsByTagId.get(tagId)?.width ?? 224;
        return sum + width + (index > 0 ? nodeGap : 0);
      }, 0)
    );
    const maxLayerWidth = Math.max(...layerWidths, 0);
    const totalLayers = orderedLayers.length;

    orderedLayers.forEach((orderedLayer, layerIndex) => {
      orderedLayer.forEach((tagId, nodeIndex) => {
        const tag = tagsById.get(tagId);
        const dimensions = dimensionsByTagId.get(tagId);
        if (!tag || !dimensions) return;

        const visualLayerIndex = layerIndex;
        const y = visualLayerIndex * layerStride + visualLayerIndex * 6 + (globalMaxNodeHeight - dimensions.height) / 2;
        const relationState = getRelationState(tagId, selectedSet, selectedRelatedSet, hoverSet);

        nodes.push({
          id: String(tag.id),
          type: 'tagNode',
          position: { x: (centerByTagId.get(tag.id) ?? 0) - dimensions.width / 2, y },
          draggable: false,
          selectable: false,
          sourcePosition: Position.Bottom,
          targetPosition: Position.Top,
          data: {
            tagId: tag.id,
            label: tag.title,
            color: resolveTagColor(tag.color),
            role: getNodeRole(tag.id, incoming, outgoing),
            sheet: tag.sheet,
            nodeCount: tag.node_count,
            degree: (incoming.get(tag.id)?.length ?? 0) + (outgoing.get(tag.id)?.length ?? 0),
            width: dimensions.width,
            height: dimensions.height,
            sourceHandles: [{ id: 'source-0', left: '50%' }],
            targetHandles: [{ id: 'target-0', left: '50%' }],
            relationState,
            revealIndex: layerIndex * 12 + nodeIndex,
          },
          style: { width: dimensions.width, height: dimensions.height },
        });
      });
    });

    const width = maxLayerWidth;
    const height = Math.max(0, totalLayers * layerStride - layerGap + globalMaxNodeHeight);

    return {
      nodes,
      width,
      height,
      score,
      orderLabel: componentTagIds.map((id) => tagsById.get(id)?.title ?? '').sort()[0] ?? '',
    };
  });

  const arrangedComponents = fullscreen
    ? componentLayouts.slice().sort((a, b) => b.score - a.score || a.orderLabel.localeCompare(b.orderLabel))
    : componentLayouts.slice().sort((a, b) => a.orderLabel.localeCompare(b.orderLabel));

  const positionedNodes: Node<BaseTagNodeData>[] = [];
  let heroCenter = { x: 0, y: 0 };

  if (fullscreen && arrangedComponents.length > 0) {
    const main = arrangedComponents[0];
    heroCenter = { x: main.width / 2, y: main.height * 0.52 };

    for (const node of main.nodes) {
      positionedNodes.push(node);
    }

    let leftCursor = -SATELLITE_COMPONENT_GAP;
    let rightCursor = main.width + SATELLITE_COMPONENT_GAP;
    const satellitePattern = [0.16, -0.22, 0.42, -0.44];

    arrangedComponents.slice(1).forEach((component, index) => {
      const placeOnRight = index % 2 === 0;
      const shiftX = placeOnRight ? rightCursor : leftCursor - component.width;
      const shiftY = Math.round(main.height * (satellitePattern[index % satellitePattern.length] ?? 0));

      for (const node of component.nodes) {
        positionedNodes.push({
          ...node,
          position: {
            x: node.position.x + shiftX,
            y: node.position.y + shiftY,
          },
        });
      }

      if (placeOnRight) rightCursor += component.width + FULLSCREEN_COMPONENT_GAP;
      else leftCursor -= component.width + FULLSCREEN_COMPONENT_GAP;
    });
  } else {
    let componentOffsetX = 0;

    arrangedComponents.forEach((component, index) => {
      const shiftX = componentOffsetX;
      const shiftY = index % 2 === 0 ? 0 : 18;

      for (const node of component.nodes) {
        positionedNodes.push({
          ...node,
          position: {
            x: node.position.x + shiftX,
            y: node.position.y + shiftY,
          },
        });
      }

      componentOffsetX += component.width + COMPONENT_GAP;
    });

    const primary = arrangedComponents[0];
    if (primary) {
      heroCenter = { x: primary.width / 2, y: primary.height * 0.52 };
    }
  }

  const minX = Math.min(...positionedNodes.map((node) => node.position.x), 0);
  const minY = Math.min(...positionedNodes.map((node) => node.position.y), 0);
  const nodes = positionedNodes.map((node) => ({
    ...node,
    position: {
      x: node.position.x - minX + GRAPH_PADDING_X,
      y: node.position.y - minY + GRAPH_PADDING_Y,
    },
  }));

  heroCenter = {
    x: heroCenter.x - minX + GRAPH_PADDING_X,
    y: heroCenter.y - minY + GRAPH_PADDING_Y,
  };

  const nodeById = new Map(nodes.map((node) => [Number(node.id), node]));
  const relationByTagId = new Map(nodes.map((node) => [Number(node.id), node.data.relationState]));
  const knownNodeIds = new Set(nodes.map((node) => Number(node.id)));
  const validEdges = overview.edges
    .filter((edge) => knownNodeIds.has(edge.source_tag_id) && knownNodeIds.has(edge.target_tag_id));

  const outgoingHandleByNode = new Map<number, Array<{ edgeIndex: number; otherX: number }>>();
  const incomingHandleByNode = new Map<number, Array<{ edgeIndex: number; otherX: number }>>();

  validEdges.forEach((edge, edgeIndex) => {
    const sourceNode = nodeById.get(edge.source_tag_id);
    const targetNode = nodeById.get(edge.target_tag_id);
    if (!sourceNode || !targetNode) return;

    const sourceCenterX = sourceNode.position.x + sourceNode.data.width / 2;
    const targetCenterX = targetNode.position.x + targetNode.data.width / 2;

    if (!outgoingHandleByNode.has(edge.source_tag_id)) outgoingHandleByNode.set(edge.source_tag_id, []);
    if (!incomingHandleByNode.has(edge.target_tag_id)) incomingHandleByNode.set(edge.target_tag_id, []);
    outgoingHandleByNode.get(edge.source_tag_id)?.push({ edgeIndex, otherX: targetCenterX });
    incomingHandleByNode.get(edge.target_tag_id)?.push({ edgeIndex, otherX: sourceCenterX });
  });

  const handleAssignmentByEdgeIndex = new Map<number, { sourceHandle?: string; targetHandle?: string }>();

  outgoingHandleByNode.forEach((entries, nodeId) => {
    const sorted = entries.slice().sort((a, b) => a.otherX - b.otherX);
    sorted.forEach((entry, index) => {
      const existing = handleAssignmentByEdgeIndex.get(entry.edgeIndex);
      handleAssignmentByEdgeIndex.set(entry.edgeIndex, {
        sourceHandle: `source-${nodeId}-${index}`,
        targetHandle: existing?.targetHandle,
      });
    });
  });

  incomingHandleByNode.forEach((entries, nodeId) => {
    const sorted = entries.slice().sort((a, b) => a.otherX - b.otherX);
    sorted.forEach((entry, index) => {
      const existing = handleAssignmentByEdgeIndex.get(entry.edgeIndex);
      handleAssignmentByEdgeIndex.set(entry.edgeIndex, {
        sourceHandle: existing?.sourceHandle,
        targetHandle: `target-${nodeId}-${index}`,
      });
    });
  });

  const routedNodes = nodes.map((node) => ({
    ...node,
    data: {
      ...node.data,
      sourceHandles: buildHandleLayout(outgoingHandleByNode.get(Number(node.id))?.length ?? 0, `source-${node.id}`),
      targetHandles: buildHandleLayout(incomingHandleByNode.get(Number(node.id))?.length ?? 0, `target-${node.id}`),
    },
  }));

  const reciprocalEdgeKeys = new Set(
    validEdges
      .filter((edge) => validEdges.some((candidate) => candidate.source_tag_id === edge.target_tag_id && candidate.target_tag_id === edge.source_tag_id))
      .map((edge) => `${edge.source_tag_id}:${edge.target_tag_id}`)
  );

  const edges: Edge<GraphEdgeData>[] = validEdges
    .map((edge, index) => {
      const sourceState = relationByTagId.get(edge.source_tag_id) ?? 'default';
      const targetState = relationByTagId.get(edge.target_tag_id) ?? 'default';
      let relationState: RelationState = 'default';

      if (sourceState === 'selected' || targetState === 'selected') relationState = 'selected';
      else if (sourceState === 'related' || targetState === 'related') relationState = 'related';
      else if (sourceState === 'preview' || targetState === 'preview') relationState = 'preview';
      else if (sourceState === 'muted' || targetState === 'muted') relationState = 'muted';

      return {
        id: `edge-${edge.source_tag_id}-${edge.target_tag_id}-${index}`,
        source: String(edge.source_tag_id),
        target: String(edge.target_tag_id),
        sourceHandle: handleAssignmentByEdgeIndex.get(index)?.sourceHandle ?? `source-${edge.source_tag_id}-0`,
        targetHandle: handleAssignmentByEdgeIndex.get(index)?.targetHandle ?? `target-${edge.target_tag_id}-0`,
        type: 'tagEdge',
        data: {
          color: nodeById.get(edge.source_tag_id)?.data.color ?? colors.pif.green,
          relationState,
          reducedMotion: false,
          hasReciprocal: reciprocalEdgeKeys.has(`${edge.source_tag_id}:${edge.target_tag_id}`),
          sourceTagId: edge.source_tag_id,
          targetTagId: edge.target_tag_id,
        },
      } satisfies Edge<GraphEdgeData>;
    });

  const orderedTags = overview.tags.slice().sort((a, b) => {
    const relationWeight = {
      selected: 0,
      related: 1,
      preview: 2,
      default: 3,
      muted: 4,
    } as const;
    const relationDelta = relationWeight[getRelationState(a.id, selectedSet, selectedRelatedSet, hoverSet)]
      - relationWeight[getRelationState(b.id, selectedSet, selectedRelatedSet, hoverSet)];
    if (relationDelta !== 0) return relationDelta;
    return compareTagIds(a.id, b.id, tagsById, incoming, outgoing, priorityByTagId);
  });

  return { nodes: routedNodes, edges, orderedTags, heroCenter };
}

const TagGraphEdge = memo(function TagGraphEdge({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  data,
}: EdgeProps<Edge<GraphEdgeData>>) {
  const isSameLevelReciprocal = Boolean(data?.hasReciprocal) && Math.abs(sourceY - targetY) < 64;
  const [smoothPath] = getSmoothStepPath({
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourcePosition,
    targetPosition,
    borderRadius: 24,
    offset: isSameLevelReciprocal ? 48 : 24,
  });
  const reciprocalDirection = (data?.sourceTagId ?? 0) < (data?.targetTagId ?? 0) ? -1 : 1;
  const reciprocalLift = 86;
  const reciprocalMidY = Math.min(sourceY, targetY) + reciprocalDirection * reciprocalLift;
  const reciprocalPath = `M ${sourceX} ${sourceY} C ${sourceX} ${reciprocalMidY}, ${targetX} ${reciprocalMidY}, ${targetX} ${targetY}`;
  const path = isSameLevelReciprocal ? reciprocalPath : smoothPath;

  const isSelected = data?.relationState === 'selected';
  const isRelated = data?.relationState === 'related';
  const isPreview = data?.relationState === 'preview';
  const isMuted = data?.relationState === 'muted';
  const edgeColor = data?.color ?? colors.pif.green;
  const stroke = isSelected
    ? edgeColor
    : isRelated
      ? edgeColor
      : isPreview
        ? withAlpha(edgeColor, 0.82)
        : withAlpha(edgeColor, isMuted ? 0.12 : 0.48);

  return (
    <>
      <BaseEdge
        id={`${id}-base`}
        path={path}
        style={{
          stroke: hexToRgba(colors.grey.medium.light, isMuted ? 0.1 : 0.34),
          strokeWidth: isSelected ? 3.4 : isRelated ? 2.6 : 1.9,
          strokeLinecap: 'round',
          strokeDasharray: isMuted ? '5 6' : undefined,
          transition: data?.reducedMotion ? undefined : 'stroke 180ms ease, stroke-width 180ms ease, opacity 180ms ease',
          opacity: isMuted ? 0.16 : 0.9,
          pointerEvents: 'none',
        }}
      />
      <BaseEdge
        id={id}
        path={path}
        style={{
          stroke,
          strokeWidth: isSelected ? 3 : isRelated ? 2.4 : isPreview ? 2 : 1.6,
          strokeLinecap: 'round',
          filter: isSelected || isRelated ? `drop-shadow(0 0 8px ${withAlpha(edgeColor, 0.26)})` : undefined,
          transition: data?.reducedMotion ? undefined : 'stroke 180ms ease, stroke-width 180ms ease, opacity 180ms ease',
          opacity: isMuted ? 0.12 : isPreview ? 0.92 : 1,
          pointerEvents: 'none',
        }}
      />
    </>
  );
});

const TagGraphNode = memo(function TagGraphNode({ data }: NodeProps<Node<TagNodeData>>) {
  const isSelected = data.relationState === 'selected';
  const isRelated = data.relationState === 'related';
  const isPreview = data.relationState === 'preview';
  const isMuted = data.relationState === 'muted';
  const isOutcome = data.role === 'outcome';
  const isSource = data.role === 'source';
  const tint = withAlpha(data.color, isSelected ? 0.16 : isRelated ? 0.1 : 0.04);
  const border = isSelected
    ? data.color
    : isRelated
      ? withAlpha(data.color, 0.4)
      : isPreview
        ? withAlpha(data.color, 0.46)
        : isOutcome
          ? withAlpha(data.color, isMuted ? 0.16 : 0.28)
          : isSource
            ? withAlpha(data.color, isMuted ? 0.14 : 0.24)
            : withAlpha(data.color, isMuted ? 0.12 : 0.22);
  return (
    <>
      {data.targetHandles.map((handle) => (
        <Handle
          key={handle.id}
          id={handle.id}
          type="target"
          position={Position.Top}
          isConnectable={false}
          className="!h-[8px] !w-[8px] !border-0 !bg-grey-black"
          style={{ opacity: 0, left: handle.left }}
        />
      ))}

      <motion.div
        role="button"
        tabIndex={0}
        className="nodrag nopan relative flex h-full w-full flex-col justify-center overflow-hidden rounded-[28px] border text-left outline-none"
        onKeyDown={(event) => {
          if (event.key !== 'Enter' && event.key !== ' ') return;
          event.preventDefault();
          data.onToggle(data.tagId);
        }}
        aria-pressed={isSelected}
        aria-label={`${data.label}${isSelected ? ', selected' : isRelated ? ', related' : ''}`}
        initial={data.reducedMotion ? { opacity: 1 } : { opacity: 0, y: 12, scale: 0.985 }}
        animate={{
          opacity: isMuted ? 0.72 : 1,
          y: 0,
          scale: isSelected ? 1.02 : isRelated ? 1.01 : 1,
        }}
        transition={data.reducedMotion
          ? { duration: 0 }
          : { duration: 0.24, ease: 'easeOut', delay: Math.min(data.revealIndex * 0.012, 0.18) }}
        style={{
          width: data.width,
          height: data.height,
          borderColor: border,
          borderWidth: isSelected ? 3 : isRelated ? 2 : isOutcome ? 1.6 : 1.25,
          backgroundColor: isSelected ? withAlpha(data.color, 0.12) : tint,
          boxShadow: isSelected
            ? `0 0 0 4px ${withAlpha(data.color, 0.14)}, 0 18px 42px rgba(17, 21, 22, 0.18)`
            : isRelated
              ? `0 0 0 2px ${withAlpha(data.color, 0.08)}, 0 14px 28px rgba(17, 21, 22, 0.11)`
              : '0 10px 22px rgba(17, 21, 22, 0.08)',
          transition: data.reducedMotion ? undefined : 'box-shadow 180ms ease, border-color 180ms ease, opacity 180ms ease',
        }}
      >
        <div className="relative z-[1] flex min-h-0 flex-1 items-center gap-3 px-5 py-4.5">
          <div className="min-w-0 flex-1">
            <div className="truncate pr-3 text-[18px] font-semibold leading-[1.05] text-grey-black">
              {data.label}
            </div>
          </div>
          <div
            className="h-10 w-2.5 shrink-0 rounded-full"
            style={{ backgroundColor: isSelected ? data.color : withAlpha(data.color, isRelated ? 0.72 : 0.52) }}
            aria-hidden
          />
        </div>
        <span className="sr-only">Press to toggle this tag in the answer focus.</span>
      </motion.div>

      {data.sourceHandles.map((handle) => (
        <Handle
          key={handle.id}
          id={handle.id}
          type="source"
          position={Position.Bottom}
          isConnectable={false}
          className="!h-[8px] !w-[8px] !border-0 !bg-grey-black"
          style={{ opacity: 0, left: handle.left }}
        />
      ))}
    </>
  );
});

function GraphAtmosphere({
  nodes,
  heroCenter,
  viewport,
  reducedMotion,
  isMoving,
}: {
  nodes: Node<BaseTagNodeData>[];
  heroCenter: { x: number; y: number };
  viewport: ViewportState;
  reducedMotion: boolean;
  isMoving: boolean;
}) {
  void nodes;
  void heroCenter;
  void viewport;
  void reducedMotion;
  void isMoving;

  return (
    <div
      className="pointer-events-none absolute inset-0 rounded-[28px]"
      aria-hidden
      style={{ backgroundColor: colors.peach.DEFAULT }}
    />
  );
}

function GraphStateCard({
  title,
  description,
  actionLabel,
  onAction,
}: {
  title: string;
  description: string;
  actionLabel?: string;
  onAction?: () => void;
}) {
  return (
      <div className="flex h-full min-h-[260px] items-center justify-center rounded-[24px] border border-peach-1 bg-white px-6 py-10 text-center shadow-[inset_0_1px_0_rgba(255,255,255,0.75)]">
      <div className="max-w-[360px]">
        <div className="text-[18px] font-semibold text-grey-black">{title}</div>
        <div className="mt-2 text-sm leading-6 text-grey-hover">{description}</div>
        {actionLabel && onAction ? (
          <button
            type="button"
            onClick={onAction}
            className="mt-5 rounded-full border border-pif-green bg-white px-4 py-2.5 text-sm font-medium text-pif-green transition-colors hover:bg-peach"
          >
            {actionLabel}
          </button>
        ) : null}
      </div>
    </div>
  );
}

const nodeTypes: NodeTypes = {
  tagNode: TagGraphNode,
};

const edgeTypes: EdgeTypes = {
  tagEdge: TagGraphEdge,
};

export const TagOverviewGraph = memo(function TagOverviewGraph({
  overview,
  selectedTagIds,
  onSelectionChange,
  loading = false,
  error,
  onRetry,
  fullscreen = false,
}: TagOverviewGraphProps) {
  const reducedMotion = usePrefersReducedMotion();
  const [viewport, setViewport] = useState<ViewportState>({ x: 0, y: 0, zoom: 1 });
  const [isMoving, setIsMoving] = useState(false);
  const handleNodeToggle = useCallback((tagId: number) => {
    const next = selectedTagIds.includes(tagId)
      ? selectedTagIds.filter((id) => id !== tagId)
      : [...selectedTagIds, tagId];

    onSelectionChange(next);
  }, [onSelectionChange, selectedTagIds]);

  const graph = useMemo(
    () => buildGraph(overview, selectedTagIds, null, fullscreen),
    [overview, selectedTagIds, fullscreen]
  );

  const renderedNodes = useMemo<Node<TagNodeData>[]>(() => {
    return graph.nodes.map((node) => ({
      ...node,
      data: {
        ...node.data,
        reducedMotion,
        fullscreen,
        onToggle: handleNodeToggle,
      },
    }));
  }, [fullscreen, graph.nodes, handleNodeToggle, reducedMotion]);

  const renderedEdges = useMemo<Edge<GraphEdgeData>[]>(() => (
    graph.edges.map((edge) => ({
      ...edge,
      data: {
        color: edge.data?.color ?? colors.pif.green,
        relationState: edge.data?.relationState ?? 'default',
        reducedMotion,
        hasReciprocal: edge.data?.hasReciprocal ?? false,
        sourceTagId: edge.data?.sourceTagId ?? Number(edge.source),
        targetTagId: edge.data?.targetTagId ?? Number(edge.target),
      },
    }))
  ), [graph.edges, reducedMotion]);

  const selectedCount = selectedTagIds.length;
  const selectedLabels = selectedTagIds
    .map((tagId) => overview.tags.find((tag) => tag.id === tagId)?.title)
    .filter((label): label is string => Boolean(label));
  const focusSummary = selectedLabels.length > 0
    ? selectedLabels.slice(0, 2).join(', ')
    : 'Click a tag to focus its connections';
  const onNodeClick = (_event: React.MouseEvent, node: Node<TagNodeData> | Node<BaseTagNodeData>) => {
    const tagId = Number(node.id);
    if (Number.isNaN(tagId)) return;
    handleNodeToggle(tagId);
  };

  if (loading) {
    return (
      <div className={fullscreen ? 'h-full w-full' : 'h-[360px] w-full'}>
        <GraphStateCard
          title="Building tag map"
          description="Loading relationships and composing the focus view for this sheet."
        />
      </div>
    );
  }

  if (error) {
    return (
      <div className={fullscreen ? 'h-full w-full' : 'h-[360px] w-full'}>
        <GraphStateCard
          title="Could not load tag map"
          description={error}
          actionLabel={onRetry ? 'Try again' : undefined}
          onAction={onRetry}
        />
      </div>
    );
  }

  if (overview.tags.length === 0) {
    return (
      <div className={fullscreen ? 'h-full w-full' : 'h-[360px] w-full'}>
        <GraphStateCard
          title="No tags available"
          description="This sheet does not have enough tag relationships to draw a focus graph yet."
        />
      </div>
    );
  }

  return (
    <div
      className={fullscreen ? 'h-full w-full' : 'h-[360px] w-full'}
      role="region"
      aria-label="Tag relationship graph"
    >
      <div className="relative h-full w-full overflow-hidden rounded-[28px] border border-peach-2 bg-peach shadow-[inset_0_1px_0_rgba(255,255,255,0.82),0_22px_56px_rgba(17,21,22,0.08)]">
        <GraphAtmosphere
          nodes={graph.nodes}
          heroCenter={graph.heroCenter}
          viewport={viewport}
          reducedMotion={reducedMotion}
          isMoving={isMoving}
        />

        <div className="absolute inset-0 rounded-[28px] bg-white/20" aria-hidden />

        <ReactFlow
          fitView
          fitViewOptions={{ padding: fullscreen ? 0.08 : 0.1, minZoom: fullscreen ? 0.68 : 0.74, maxZoom: fullscreen ? 1.72 : 1.18 }}
          nodes={renderedNodes}
          edges={renderedEdges}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          onNodeClick={onNodeClick}
          nodesDraggable={false}
          nodesConnectable={false}
          nodesFocusable={false}
          elementsSelectable={false}
          selectNodesOnDrag={false}
          panOnDrag={fullscreen}
          panOnScroll={fullscreen}
          zoomOnPinch
          zoomOnScroll={fullscreen}
          zoomOnDoubleClick={false}
          elevateNodesOnSelect={false}
          proOptions={{ hideAttribution: true }}
          defaultEdgeOptions={{ interactionWidth: 1 }}
          onMoveStart={() => setIsMoving(true)}
          onMove={(_, nextViewport) => {
            setViewport({ x: nextViewport.x, y: nextViewport.y, zoom: nextViewport.zoom });
          }}
          onMoveEnd={(_, nextViewport) => {
            setIsMoving(false);
            setViewport({ x: nextViewport.x, y: nextViewport.y, zoom: nextViewport.zoom });
          }}
        >
          <Background color="rgba(202, 193, 175, 0.14)" gap={34} size={1.05} />
          <Panel position="top-left">
            <div className="rounded-[20px] border border-[rgba(38,91,78,0.1)] bg-white px-4 py-3 shadow-[0_14px_36px_rgba(17,21,22,0.08)]">
              <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-pif-green">Tag focus</div>
              <div className="mt-1 text-[15px] font-semibold text-grey-black">
                {selectedCount > 0 ? focusSummary : 'Click a tag to focus answers'}
              </div>
              <div className="mt-1 text-[12px] text-grey-hover">
                {selectedCount > 0
                  ? `${selectedCount} selected. Connected lines stay bright.`
                  : fullscreen
                    ? 'Click to isolate relationships.'
                    : 'Click a tag to isolate its relationships.'}
              </div>
              {selectedCount > 0 ? (
                <button
                  type="button"
                  onClick={() => onSelectionChange([])}
                  className="mt-3 rounded-full border border-[rgba(38,91,78,0.14)] bg-white px-3 py-1.5 text-[12px] font-medium text-pif-green transition-colors hover:bg-peach"
                >
                  Clear focus
                </button>
              ) : null}
            </div>
          </Panel>
        </ReactFlow>

        <div className="sr-only" aria-live="polite">
          {selectedCount > 0
            ? `${selectedCount} tags selected in the graph.`
            : 'No tags currently selected in the graph.'}
        </div>
      </div>
    </div>
  );
});

export default TagOverviewGraph;
